import {
  createAssistantRealtimeBridge,
  hasSeenAssistantRealtimeEvent,
  assistantRealtimeActions,
  type AssistantRealtimeBridgeEvent,
} from '@triedge/rtk-abstractions';
import {
  IntellioAssistantRealtimeEventSchema,
  IntellioAssistantRunRecordSchema,
  IntellioAssistantWorkItemRecordSchema,
  type IntellioAssistantRunRecord,
} from '@triedge/domain-core';
import { getAmplifyClient } from '../amplifyClient';
import { domainRouterApi, type NotificationItem, type PortalNotificationQueryArgs } from './domainRouterApi';
import type { AppDispatch, RootState } from './store';

const ON_CREATE_ASSISTANT_REALTIME_EVENT_SUBSCRIPTION = /* GraphQL */ `
  subscription OnCreateAssistantRealtimeEvent($actorId: String!) {
    onCreateAssistantRealtimeEvent(filter: { actorId: { eq: $actorId } }) {
      id
      actorId
      tenantId
      kind
      module
      route
      runId
      workItemId
      notificationId
      state
      title
      summary
      priority
      sourceRef
      traceId
      sequence
      payloadJson
      occurredAt
    }
  }
`;

type RealtimeBridgeStartInput = {
  dispatch: AppDispatch;
  getState: () => RootState;
  actorId: string;
  familyId?: string;
  pathname?: string;
  persona?: string;
  listQueryArgs: PortalNotificationQueryArgs;
};

type JsonRecord = Record<string, unknown>;

type NotificationLike = JsonRecord & {
  id: string;
  title: string;
  module: string;
  route: string;
};

type SharedAssistantRealtimeSubscription = {
  refCount: number;
  subscription: { unsubscribe: () => void };
};

const sharedAssistantRealtimeSubscriptions = new Map<string, SharedAssistantRealtimeSubscription>();

const isRecord = (value: unknown): value is JsonRecord => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const parseJson = (value: unknown): unknown => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const toInteger = (value: unknown, fallback: number): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : fallback;
};

const toNotificationRecord = (value: unknown): NotificationLike | null => {
  if (!isRecord(value)) {
    return null;
  }
  const id = String(value.id ?? '').trim();
  if (!id) {
    return null;
  }
  return {
    ...value,
    id,
    title: String(value.title ?? 'Notification'),
    module: String(value.module ?? 'admissions'),
    route: typeof value.route === 'string' && value.route.trim()
      ? value.route
      : '/school/admissions/dashboard',
  };
};

const toErrorMessage = (error: unknown): string => (
  error instanceof Error ? error.message : String(error ?? 'assistant subscription failed')
);

const isNonBlockingSubscriptionError = (error: unknown): boolean => (
  /FieldUndefined|Cannot query field|is undefined|Unknown operation|Unknown field|Validation error of type|onCreateAssistantRealtimeEvent/i
    .test(toErrorMessage(error))
);

const parseEventPayload = (event: ReturnType<typeof IntellioAssistantRealtimeEventSchema.parse>): AssistantRealtimeBridgeEvent => {
  const payload = parseJson(event.payloadJson ?? null);
  const receivedAtEpochMs = Date.now();
  let workItem = null as AssistantRealtimeBridgeEvent['workItem'];
  let run = null as AssistantRealtimeBridgeEvent['run'];
  let notification = null as AssistantRealtimeBridgeEvent['notification'];

  if (event.kind.startsWith('work.')) {
    const parsed = IntellioAssistantWorkItemRecordSchema.safeParse(payload);
    workItem = parsed.success ? parsed.data : null;
  }

  if (event.kind === 'notification.projected') {
    notification = toNotificationRecord(payload);
  }

  if (event.kind.startsWith('run.')) {
    if (isRecord(payload) && isRecord(payload.run)) {
      const parsedRun = IntellioAssistantRunRecordSchema.safeParse(payload.run);
      if (parsedRun.success) {
        run = {
          run: parsedRun.data,
          response: isRecord(payload.response) ? payload.response : null,
        };
      }
    } else {
      const parsedRun = IntellioAssistantRunRecordSchema.safeParse(payload);
      if (parsedRun.success) {
        run = {
          run: parsedRun.data,
          response: null,
        };
      }
    }
  }

  return {
    event,
    workItem,
    run,
    notification,
    receivedAtEpochMs,
  };
};

const upsertNotificationIntoConnection = (
  draft: { items: NotificationItem[]; totalCount: number },
  notification: NotificationLike,
): void => {
  const next = notification as unknown as NotificationItem;
  const existingIndex = draft.items.findIndex((entry) => String(entry.id ?? '') === String(next.id ?? ''));
  if (existingIndex >= 0) {
    draft.items[existingIndex] = {
      ...draft.items[existingIndex],
      ...next,
    } as NotificationItem;
  } else {
    draft.items.unshift(next);
    draft.totalCount += 1;
  }

  draft.items.sort((left, right) => {
    const leftCreatedAt = Number((left as Record<string, unknown>).createdAt ?? 0);
    const rightCreatedAt = Number((right as Record<string, unknown>).createdAt ?? 0);
    return rightCreatedAt - leftCreatedAt;
  });
};

const markNotificationAsReadInConnection = (
  draft: { items: NotificationItem[] },
  notificationId: string,
  readAtEpochSeconds: number,
): void => {
  const index = draft.items.findIndex((entry) => String(entry.id ?? '') === notificationId);
  if (index < 0) {
    return;
  }
  const current = draft.items[index] as Record<string, unknown>;
  draft.items[index] = {
    ...current,
    deliveryState: 'read',
    readAt: readAtEpochSeconds,
    actorState: {
      ...(isRecord(current.actorState) ? current.actorState : {}),
      unreadCount: 0,
      lastReadAt: readAtEpochSeconds,
    },
  } as NotificationItem;
};

const patchNotificationCachesForEvent = (input: {
  dispatch: AppDispatch;
  listQueryArgs: PortalNotificationQueryArgs;
  bridgeEvent: AssistantRealtimeBridgeEvent;
}) => {
  const notification = input.bridgeEvent.notification;
  if (notification) {
    input.dispatch(
      domainRouterApi.util.updateQueryData('listNotifications', input.listQueryArgs, (draft) => {
        upsertNotificationIntoConnection(draft as { items: NotificationItem[]; totalCount: number }, notification as NotificationLike);
      })
    );
    return;
  }

  if (
    (input.bridgeEvent.event.kind === 'work.dismissed' || input.bridgeEvent.event.kind === 'work.completed')
    && input.bridgeEvent.event.notificationId
  ) {
    input.dispatch(
      domainRouterApi.util.updateQueryData('listNotifications', input.listQueryArgs, (draft) => {
        markNotificationAsReadInConnection(
          draft as { items: NotificationItem[] },
          String(input.bridgeEvent.event.notificationId),
          Math.floor(input.bridgeEvent.receivedAtEpochMs / 1000),
        );
      })
    );
  }
};

const extractActiveRun = (value: unknown): { run: IntellioAssistantRunRecord; response: Record<string, unknown> | null } | null => {
  if (!isRecord(value)) {
    return null;
  }
  const parsed = IntellioAssistantRunRecordSchema.safeParse(value);
  return parsed.success
    ? {
        run: parsed.data,
        response: isRecord(parsed.data.response) ? parsed.data.response : null,
      }
    : null;
};

const isSuccessfulParse = <T,>(
  result: { success: true; data: T } | { success: false }
): result is { success: true; data: T } => result.success;

export const hydrateAssistantSessionState = (input: {
  dispatch: AppDispatch;
  payload: unknown;
}) => {
  const root = isRecord(input.payload) ? input.payload : {};
  const workItemsRaw = Array.isArray(root.workItems) ? root.workItems : [];
  const workItems = workItemsRaw
    .map((item) => IntellioAssistantWorkItemRecordSchema.safeParse(item))
    .filter(isSuccessfulParse)
    .map((result) => result.data);
  const activeRunCandidate = root.activeRun ?? null;
  const activeRun = extractActiveRun(activeRunCandidate);

  input.dispatch(assistantRealtimeActions.sessionHydrated({
    workItems,
    activeRun: activeRun?.run ?? null,
    response: activeRun?.response ?? null,
    nowEpochMs: Date.now(),
  }));
};

export const startYstnjAssistantRealtimeBridge = (input: RealtimeBridgeStartInput) => {
  const actorId = String(input.actorId ?? '').trim();
  if (!actorId) {
    return {
      unsubscribe: () => {
        // no-op
      },
    };
  }

  const client = getAmplifyClient();
  const bridge = createAssistantRealtimeBridge({
    subscription: {
      appId: 'ystnj',
      moduleId: 'assistant',
      auth: {
        authMode: 'userPool',
        requiresAuthenticatedSession: true,
        allowedPersonas: ['staff', 'parent', 'admin', 'owner', 'viewer', 'editor', 'nurse', 'super-administrator'],
      },
      open: ({ actorId: targetActorId, onEvent, onError }) => {
        const stream = (client as {
          graphql: (request: unknown) => {
            subscribe: (handlers: {
              next?: (value: unknown) => void;
              error?: (error: unknown) => void;
            }) => { unsubscribe: () => void };
          };
        }).graphql({
          query: ON_CREATE_ASSISTANT_REALTIME_EVENT_SUBSCRIPTION,
          variables: { actorId: targetActorId },
          authMode: 'userPool',
        });

        const subscription = stream.subscribe({
          next: (value) => {
            const payload = value as {
              data?: {
                onCreateAssistantRealtimeEvent?: Record<string, unknown>;
              };
            };
            const rawEvent = payload.data?.onCreateAssistantRealtimeEvent;
            if (!rawEvent || typeof rawEvent !== 'object') {
              return;
            }
            const parsed = IntellioAssistantRealtimeEventSchema.safeParse({
              ...rawEvent,
              occurredAt: toInteger(rawEvent.occurredAt, Math.floor(Date.now() / 1000)),
            });
            if (!parsed.success) {
              return;
            }
            onEvent(parseEventPayload(parsed.data));
          },
          error: (error) => {
            const normalizedError = error instanceof Error
              ? error
              : new Error(String(error ?? 'assistant subscription failed'));
            if (isNonBlockingSubscriptionError(normalizedError)) {
              input.dispatch(assistantRealtimeActions.connectionStateChanged({
                status: 'disconnected',
                errorMessage: normalizedError.message,
                nowEpochMs: Date.now(),
              }));
              return;
            }
            onError(normalizedError);
          },
        });

        return {
          unsubscribe: () => {
            subscription.unsubscribe();
          },
        };
      },
    },
    store: {
      hasSeenEvent: ({ eventId }) => hasSeenAssistantRealtimeEvent(input.getState().assistantRealtime, { eventId }),
      eventReceived: (bridgeEvent) => {
        patchNotificationCachesForEvent({
          dispatch: input.dispatch,
          listQueryArgs: input.listQueryArgs,
          bridgeEvent,
        });
        input.dispatch(assistantRealtimeActions.eventReceived(bridgeEvent));
      },
      connectionStateChanged: ({ status, errorMessage, nowEpochMs }) => {
        input.dispatch(assistantRealtimeActions.connectionStateChanged({
          status,
          errorMessage,
          nowEpochMs,
        }));
      },
    },
  });

  return bridge.start({
    actorId,
    pathname: input.pathname,
    persona: input.persona,
  });
};

export const retainYstnjAssistantRealtimeBridge = (input: RealtimeBridgeStartInput) => {
  const actorId = String(input.actorId ?? '').trim();
  const familyId = String(input.familyId ?? input.listQueryArgs.familyId ?? '').trim();
  if (!actorId) {
    return {
      unsubscribe: () => {
        // no-op
      },
    };
  }

  const subscriptionKey = `${actorId}::${familyId}`;
  const existing = sharedAssistantRealtimeSubscriptions.get(subscriptionKey);
  if (existing) {
    existing.refCount += 1;
    return {
      unsubscribe: () => {
        const current = sharedAssistantRealtimeSubscriptions.get(subscriptionKey);
        if (!current) {
          return;
        }
        current.refCount -= 1;
        if (current.refCount > 0) {
          return;
        }
        sharedAssistantRealtimeSubscriptions.delete(subscriptionKey);
        current.subscription.unsubscribe();
      },
    };
  }

  const subscription = startYstnjAssistantRealtimeBridge(input);
  sharedAssistantRealtimeSubscriptions.set(subscriptionKey, {
    refCount: 1,
    subscription,
  });

  return {
    unsubscribe: () => {
      const current = sharedAssistantRealtimeSubscriptions.get(subscriptionKey);
      if (!current) {
        return;
      }
      current.refCount -= 1;
      if (current.refCount > 0) {
        return;
      }
      sharedAssistantRealtimeSubscriptions.delete(subscriptionKey);
      current.subscription.unsubscribe();
    },
  };
};
